/**
 * Presentation-agnostic Pagination model: page math, the page-number token
 * list, and the event handlers consumed identically by PaginationTailwind and
 * PaginationStyled. Contains NO rendering — the JSX and the `return null` guard
 * stay in each component — so the two implementations cannot drift apart. Call
 * it unconditionally at the top of each component, before any early return.
 */

import * as React from "react";
import type { PaginationProps } from "./PaginationTypes";

export const usePaginationModel = ({
  paginationConfig,
  currentPageIndex,
  pageSize,
  totalRows,
  onPageChange,
  onPageSizeChange,
}: PaginationProps) => {
  const { displayType, pageSizeOptions, tableCountFunction } = paginationConfig;

  // Calculate pagination values
  const totalPages = Math.ceil(totalRows / pageSize);
  const currentPage = currentPageIndex + 1; // Convert 0-based to 1-based for display
  const startRow = totalRows === 0 ? 0 : currentPageIndex * pageSize + 1;
  const endRow = Math.min((currentPageIndex + 1) * pageSize, totalRows);

  const canGoPrevious = currentPageIndex > 0;
  const canGoNext = currentPageIndex < totalPages - 1;

  const handlePrevious = () => {
    if (canGoPrevious) {
      onPageChange(currentPageIndex - 1);
    }
  };

  const handleNext = () => {
    if (canGoNext) {
      onPageChange(currentPageIndex + 1);
    }
  };

  const handlePageSizeChange = (e: React.SyntheticEvent<HTMLSelectElement>) => {
    const newPageSize = Number(e.currentTarget.value);
    onPageSizeChange(newPageSize);
    // Reset to first page when page size changes
    onPageChange(0);
  };

  // Generate page number buttons using web-app-core's algorithm
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const size = paginationConfig.size || "default";

    // Configuration for how many pages to show around current page
    const NUM_PAGE_LOWER = size === "mini" ? 0 : 1;
    const NUM_PAGE_UPPER = size === "mini" ? 0 : 2;

    // If number of pages is small enough, show all the page tokens without ellipsis
    if (totalPages <= 5 + NUM_PAGE_LOWER + NUM_PAGE_UPPER) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Add first page
      pages.push(1);

      // Add ellipsis at the beginning if needed
      if (currentPage > 3 + NUM_PAGE_LOWER) {
        pages.push("...");
      }

      // Add pages in the middle
      let start: number;
      let end: number; // inclusive index

      // If near beginning - Show number of pages before ending ellipsis
      if (currentPage <= 3 + NUM_PAGE_LOWER) {
        start = 2;
        end = 3 + NUM_PAGE_LOWER + NUM_PAGE_UPPER;
      } else if (currentPage > totalPages - (3 + NUM_PAGE_UPPER)) {
        // If near end - Show number of pages after starting ellipsis
        start = totalPages - (2 + NUM_PAGE_LOWER + NUM_PAGE_UPPER);
        end = totalPages - 1;
      } else {
        // If in the middle - Show number of pages between two ellipses
        start = currentPage - NUM_PAGE_LOWER;
        end = currentPage + NUM_PAGE_UPPER;
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add a final ellipsis if necessary
      if (currentPage < totalPages - (2 + NUM_PAGE_UPPER)) {
        pages.push("...");
      }

      // Add the last page number
      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return {
    displayType,
    pageSizeOptions,
    tableCountFunction,
    totalPages,
    currentPage,
    startRow,
    endRow,
    canGoPrevious,
    canGoNext,
    handlePrevious,
    handleNext,
    handlePageSizeChange,
    pageNumbers,
  };
};
